import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { db, auth } from '../firebase';
import { collection, addDoc, doc, updateDoc, increment } from 'firebase/firestore';
import BackHomeButton from '../components/BackHomeButton';

const SameQuiz = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { Quiz_Text, subject, topic, format, standard, lesson, school, originalQuizId } = location.state || {};

  const [allQuestions, setAllQuestions] = useState([]);
  const [currentQuestion, setCurrentQuestion] = useState(null);
  const [questionIndex, setQuestionIndex] = useState(0);
  const [userAnswers, setUserAnswers] = useState([]);
  const [selectedOption, setSelectedOption] = useState('');
  const [isFinished, setIsFinished] = useState(false);
  const [score, setScore] = useState(0);
  
  const [timeLeft, setTimeLeft] = useState(30);
  const [qloading, setqLoading] = useState(false);
  const countdownTimeoutRef = useRef(null);

  // Parse existing quiz text on load
  useEffect(() => {
    if (!Quiz_Text || Quiz_Text === 'No_Content') {
      alert("No quiz data found to retake.");
      navigate('/Old_Home');
      return;
    }

    const parseAIResponse = (responseText) => {
      const lines = responseText.trim().split('\n');
      const questionLine = lines.find(line => line.toLowerCase().startsWith('question:'));
      const answerLine = lines.find(line => line.toLowerCase().startsWith('answer:'));
      const optionsLine = format === 'MCQ' || format === 'PatternMaths' ? lines.find(line => line.toLowerCase().startsWith('options:')) : null;

      let options = [];
      if (optionsLine) {
        options = optionsLine.replace(/options:/i, '').split('|').map(opt => opt.trim());
      }

      return {
        question: questionLine?.replace(/question:/i, '').trim(),
        type: format === 'MCQ' ? 'mcq' : 'text',
        options,
        answer: answerLine?.replace(/answer:/i, '').trim(),
      };
    };

    const questionsArray = Quiz_Text.split('\n\n').map(qBlock => parseAIResponse(qBlock)).filter(Boolean);
    setAllQuestions(questionsArray);
    if (questionsArray.length > 0) setCurrentQuestion(questionsArray[0]);
  }, [Quiz_Text, format, navigate]);

  // Timer Logic
  useEffect(() => {
    if (isFinished || !currentQuestion) return;
    setTimeLeft(30); 
    
    const tick = () => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          handleSubmitAnswer(true);
          return 0;
        }
        return prev - 1;
      });
    };

    countdownTimeoutRef.current = setInterval(tick, 1000);
    return () => clearInterval(countdownTimeoutRef.current);
  }, [questionIndex, currentQuestion, isFinished]);

  const handleSubmitAnswer = async (isAuto = false) => {
    if (qloading) return;
    setqLoading(true);

    const userAns = (selectedOption || '').toString();
    const isCorrect = userAns.toLowerCase().trim() === (currentQuestion.answer || '').toLowerCase().trim();

    const result = {
      question: currentQuestion.question,
      userAnswer: userAns,
      correctAnswer: currentQuestion.answer,
      isCorrect,
    };

    const finalAnswers = [...userAnswers, result];
    setUserAnswers(finalAnswers);
    if (isCorrect) setScore(prev => prev + 1);

    if (questionIndex + 1 >= allQuestions.length) {
      setIsFinished(true);
      await saveResults(finalAnswers);
    } else {
      setSelectedOption('');
      setQuestionIndex(prev => prev + 1);
      setCurrentQuestion(allQuestions[questionIndex + 1]);
    }
    setqLoading(false);
  };

const saveResults = async (finalAnswers) => {
    const finalScore = finalAnswers.filter(ans => ans.isCorrect).length;
    try {
      // 1. Log the new result
      await addDoc(collection(db, 'AI_Results'), {
        uid: auth?.currentUser?.uid || "Unknown",
        subject: subject || "Unknown",
        topic: topic || "Unknown",
        format: format || "Unknown",
        lesson: lesson || "Unknown",
        standard: standard || "Unknown",
        school: school || "Unknown", // 👈 Now included!
        score: finalScore,
        totalQuestions: finalAnswers.length,
        answers: finalAnswers,
        timestamp: new Date(),
        Quiz_Text: Quiz_Text || "No_Content", 
        isRetake: true
      });

      // 2. Increment counter on original document
      if (originalQuizId) {
        await updateDoc(doc(db, "AI_Results", originalQuizId), {
          retake_count: increment(1)
        });
      }
    } catch (err) {
      console.error("Error saving retake:", err);
    }
  };

if (isFinished) {
    return (
      <div className="min-h-screen bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-indigo-50 via-slate-50 to-white py-4 sm:py-8 px-3 sm:px-6 lg:px-8 font-sans relative">
        <div className="w-full max-w-4xl mx-auto bg-white/80 backdrop-blur-xl rounded-[1rem] sm:rounded-[2rem] shadow-2xl shadow-indigo-500/10 border border-white p-4 sm:p-8">
          <div className="text-center mb-6 sm:mb-10">
            <h2 className="text-2xl sm:text-4xl font-extrabold text-slate-900 mb-2">Retake Completed 🎉</h2>
            <div className="inline-block bg-indigo-100 text-indigo-800 px-4 sm:px-6 py-2 sm:py-3 rounded-full font-black text-lg sm:text-2xl shadow-sm">
              Your Score: {score} / {allQuestions.length}
            </div>
          </div>

          <h3 className="text-sm sm:text-lg font-bold text-slate-700 mb-4 border-b border-slate-200 pb-2">Answer Breakdown:</h3>
          
          <div className="space-y-3 sm:space-y-4 mb-8">
            {userAnswers.map((ans, index) => (
              <div key={index} className={`p-4 sm:p-5 rounded-xl sm:rounded-2xl border-2 transition-all ${ans.isCorrect ? 'bg-emerald-50 border-emerald-200' : 'bg-rose-50 border-rose-200'}`}>
                <p className="font-bold text-slate-800 text-[11px] sm:text-sm mb-2"><span className="text-indigo-500 mr-1">Q{index + 1}.</span> {ans.question}</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-[10px] sm:text-sm">
                  <p className="text-slate-600"><span className="font-semibold text-slate-500">Your Answer:</span> {ans.userAnswer || <span className="italic text-slate-400">Blank</span>}</p>
                  <p className="text-slate-600"><span className="font-semibold text-slate-500">Correct Answer:</span> {ans.correctAnswer}</p>
                </div>
                <div className={`mt-3 inline-flex items-center px-2.5 py-1 rounded-md text-[9px] sm:text-xs font-bold uppercase tracking-wider ${ans.isCorrect ? 'bg-emerald-200 text-emerald-800' : 'bg-rose-200 text-rose-800'}`}>
                  {ans.isCorrect ? '✅ Correct' : '❌ Incorrect'}
                </div>
              </div>
            ))}
          </div>

          {/* Share Button Block */}
          <div className="flex justify-center mb-6">
            <button 
              onClick={() => navigate('/Email_Quiz', { 
                state: { 
                  Quiz_Text: typeof Quiz_Text !== 'undefined' ? Quiz_Text : Quiz_Text, 
                  subject, 
                  topic, 
                  format, 
                  standard, 
                  lesson, 
                  school 
                }
              })}
              className="bg-violet-500 hover:bg-violet-600 text-white font-bold py-3 px-6 rounded-xl shadow-md transition-all flex items-center gap-2 active:scale-95"
            >
              ↗️ Share this Quiz
            </button>
          </div>

          <div className="flex justify-center">
            <BackHomeButton />
          </div>
        </div>
      </div>
    );
  }

  if (!currentQuestion) return <div style={{ textAlign: 'center', marginTop: '50px' }}>Loading Quiz...</div>;

  return (
    <div style={{ maxWidth: '600px', margin: '40px auto', padding: '30px', background: '#fff', borderRadius: '16px', boxShadow: '0 4px 20px rgba(0,0,0,0.1)', fontFamily: 'Arial, sans-serif' }}>
      
      <BackHomeButton />
      
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '20px', fontWeight: 'bold', color: '#666' }}>
        <span>Question {questionIndex + 1} of {allQuestions.length}</span>
        <span style={{ color: '#d32f2f' }}>⏳ {timeLeft}s</span>
      </div>
      
      <h2 style={{ marginBottom: '20px', color: '#333' }}>{currentQuestion.question}</h2>

      <div style={{ marginBottom: '20px' }}>
        {currentQuestion.options && currentQuestion.options.length > 0 ? (
          currentQuestion.options.map((opt, i) => (
            <button key={i} onClick={() => setSelectedOption(opt)}
              style={{
                display: 'block', width: '100%', textAlign: 'left', padding: '12px 16px', marginBottom: '12px',
                background: selectedOption === opt ? '#4caf50' : '#f5f5f5',
                color: selectedOption === opt ? '#fff' : '#333',
                border: selectedOption === opt ? '2px solid #388e3c' : '1px solid #ccc',
                borderRadius: '10px', cursor: 'pointer', fontWeight: '500'
              }}>
              {opt}
            </button>
          ))
        ) : (
           <input type="text" placeholder="Type answer..." value={selectedOption} onChange={(e) => setSelectedOption(e.target.value)} 
            style={{ width: '100%', padding: '12px', border: '1px solid #ccc', borderRadius: '10px', fontSize: '1rem' }} />
        )}
      </div>

      <button onClick={() => handleSubmitAnswer(false)} disabled={qloading || !selectedOption} 
        style={{
          background: '#4caf50', color: '#fff', padding: '12px 24px', border: 'none', borderRadius: '10px',
          fontSize: '1rem', cursor: (qloading || !selectedOption) ? 'not-allowed' : 'pointer', width: '100%', opacity: (!selectedOption) ? 0.5 : 1
        }}>
        {questionIndex + 1 === allQuestions.length ? 'Finish' : 'Next'}
      </button>
    </div>
  );
};

export default SameQuiz;